import moment from 'moment';
import Raven from 'raven-js';
import {
  VirtualChallengeType,
  VirtualChallenges,
  sport,
} from '@letsdothis/reference-data';
import map from 'lodash/map';
import sum from 'lodash/sum';

import { useAuthService } from '@/contexts/authService';

import getUrl from '@/routes/routes/helpers/getUrl';
import { KM_PER_MILE } from '../../../state/constants';

import { hasRegistrationPerks } from '../../../utils/race';
import { getRaceOnEvent } from '../../../utils/ticket';

export const getEventId = (e) => e.id;

export const isEventBookable = (e) => hasRegistrationPerks(e?.races || {});

export const isEventWithVirtualSubmissionsEnabled = (e) =>
  !!e?.virtualEventDetails?.enableVirtualResultSubmission;

export const isHackedSeriesEvent = (e) => {
  // https://lets-dothis.slack.com/archives/C0103LE7T0Q/p1588459037061700 (187600)
  // Determine events like https://www.letsdothis.com/us/e/social-distance-run-series-presented-by-kaiser-permanente-187566
  // that don't have each race being a new week i.e. non official series
  const seriesEvents = [187566, 187600, 81197];

  return seriesEvents.includes(e.id);
};

export const isMultiSubmissionHackNeeded = (e) => {
  // https://lets-dothis.slack.com/archives/CS2NWJ5QE/p1588800638018100 (187590)
  // Booking should allow for a ticket that you can submit for all races on.
  const eventsThatShouldAllowMultipleSubmissionsOnSameEvent = [
    187590,
    11120361, // Integration test events
  ];

  return (
    isHackedSeriesEvent(e) ||
    eventsThatShouldAllowMultipleSubmissionsOnSameEvent.includes(e.id)
  );
};

export const getEventLink = (e) => e.permalink;

export const getResultSubmissionsLink = (e) =>
  `${getEventLink(e)}/result-submissions`;

export const getNoBookingSubmitResultLink = (e) =>
  `${getEventLink(e)}/submit-result`;

export const getResultSubmissionConfirmationLink = (e, submissionId) =>
  `${getEventLink(e)}/result/${submissionId}`;

export const getResultSubmissionEditLink = (e, submissionId) =>
  `${getResultSubmissionConfirmationLink(e, submissionId)}/edit`;

export const getSubmissionsForBookingLink = (e, bookingId) =>
  `${getEventLink(e)}/result-submissions/${bookingId}`;

export const getSubmitResultForBookingLink = (e, bookingId) =>
  `${getEventLink(e)}/submit-result/booking/${bookingId}`;

export const getSubmissionIndexQuery = (submissionIndex) =>
  typeof submissionIndex === 'number' && !Number.isNaN(submissionIndex)
    ? `?submissionIndex=${submissionIndex}`
    : '';

export const getBookingResultSubmissionConfirmationLink = (
  e,
  fullBookingId,
  submissionIndex,
) =>
  `${getEventLink(e)}/result/booking/${fullBookingId}${getSubmissionIndexQuery(
    submissionIndex,
  )}`;

export const getBookingResultSubmissionEditLink = (
  e,
  fullBookingId,
  submissionIndex,
) =>
  `${getBookingResultSubmissionConfirmationLink(
    e,
    fullBookingId,
  )}/edit${getSubmissionIndexQuery(submissionIndex)}`;

export const getVirtualEventLink = (e) => {
  const authService = useAuthService();
  // Until we can support email login i.e. login for all, events booked outside of LDT
  // must still let participants submit without logging in.
  // We can hopefully remove this condition in the future.
  if (!isEventBookable(e) && !authService.loggedIn()) {
    return getNoBookingSubmitResultLink(e);
  }

  return getResultSubmissionsLink(e);
};

export const getExternalBookingWebsiteLink = (e, raceId = null) => {
  const races = e?.races?.races;
  const raceDone = raceId
    ? races?.find((race) => race?.id === raceId)
    : races[0];
  const link =
    raceDone?.tickets?.tickets?.[0]?.bookingUrl ||
    getUrl.eventPage(null, null, e?.id);
  return link;
};

export const getResultsLink = (e) => `${getEventLink(e)}/results`;

export const getResultsLinkWithLeaderboardOpen = (e) =>
  `${getResultsLink(e)}?leaderboard`;

export const getResultsLinkWithGalleryOpen = (e) =>
  `${getResultsLink(e)}?gallery`;

export const getVirtualEventUrl = (e) => `${WEB_ROOT}${getEventLink(e)}`;

export const getVirtualEventResultsUrl = (e) =>
  `${WEB_ROOT}${getResultsLink(e)}`;

export const getEventTitle = (e) => e.title;

export const getOrganiserName = (e) => e.organiser?.title;

export const getOrganiserMessage = (e) => e.organiserMessage;

export const getSubmissionWindowFromDates = (
  submissionStartDate,
  submissionEndDate,
) => {
  const start = moment(submissionStartDate);
  const end = moment(submissionEndDate);

  const isBefore = start.isAfter(moment());
  const isAfter = moment().isAfter(end);
  const isLive = !isBefore && !isAfter;

  const stage =
    (isBefore && 'before') ||
    (isAfter && 'after') ||
    (isLive && 'live') ||
    null;

  return { stage, isAfter, isBefore, isLive, start, end };
};

export const getSubmissionWindow = (e) => {
  const { submissionStartDate, submissionEndDate } = e.virtualEventDetails;

  return getSubmissionWindowFromDates(submissionStartDate, submissionEndDate);
};

export const getDateRange = (event) => {
  const { start, end } = getSubmissionWindow(event);
  const justBeforeEnd = end.clone().subtract(1, 'minute');
  const startSuffix = [
    justBeforeEnd.month() !== start.month() && ' MMM',
    justBeforeEnd.year() !== start.year() && ' YYYY',
  ]
    .filter((i) => i)
    .join('');

  return `${start.format(`ddd Do${startSuffix}`)} - ${justBeforeEnd?.format(
    'ddd Do MMM YYYY',
  )}`;
};

export const getRaces = (e) => {
  const races = e.races?.races || [];
  return races.filter((r) => r?.id && r?.title);
};

export const getRaceTitles = (event) => {
  const races = getRaces(event);
  return races.map((r) => r.title);
};

export const getRaceByRaceId = (event, raceId) =>
  getRaces(event).find((race) => race.id === raceId);

export const getRaceIdByTitle = (event, title) =>
  getRaces(event)?.find((race) => race.title === title)?.id;

export const canSubmitMultipleForBooking = (event) => {
  // For integration tests.
  // See ./cypress/integration/virtual_event_submission_from_booking_spec.js
  const eventChallengeType = event?.virtualEventDetails?.challengeType;
  if (eventChallengeType in VirtualChallengeType) {
    return VirtualChallenges[eventChallengeType].allowedSubmissions > 1;
  }
  return event?.id === 11120357;
};

export const challengeSubmissionState = (event, resultSubmissionsByEvent) => {
  const {
    hasSubmissions,
    personalResultSubmissions,
    allBookingsAllParticipantsSubmitted,
  } = resultSubmissionsByEvent;

  const eventChallengeType = event?.virtualEventDetails?.challengeType;
  const eventId = event.id;

  if (eventChallengeType === VirtualChallengeType.GOAL_DISTANCE_CHALLENGE) {
    const personalEventSubmissions = personalResultSubmissions.filter(
      (resultSubmission) => resultSubmission.eventId === eventId,
    );

    if (personalEventSubmissions.length === 0) {
      return { hasCompleted: false, hasSubmittedResults: false };
    }
    if (personalEventSubmissions.length > 1) {
      Raven.captureMessage(
        'MULTIPLE RACES/BOOKINGS ENTERED FOR DISTANCE CHALLENGE EVENT, CTA state will look broken',
      );
    }
    const raceId = personalEventSubmissions[0].raceId;
    const matchingRace = event?.races?.races?.find(
      (race) => race.id === raceId,
    );
    const targetDistanceM =
      matchingRace?.information?.metricLength * 1000 ?? Infinity;
    const achievedDistanceM =
      personalEventSubmissions?.[0]?.metrics?.distance ?? 0;

    if (achievedDistanceM >= targetDistanceM)
      return { hasCompleted: true, hasSubmittedResults: true };
    return { hasCompleted: false, hasSubmittedResults: true };
  }

  const eventNeedsMultipleSubmissionHack = isMultiSubmissionHackNeeded(event);
  const hasSubmittedResults = eventNeedsMultipleSubmissionHack
    ? false
    : hasSubmissions;
  const hasCompleted =
    hasSubmittedResults && allBookingsAllParticipantsSubmitted;
  return { hasCompleted, hasSubmittedResults };
};

export const getRaceInfo = (submission, event) => {
  const race = getRaceOnEvent(event, submission?.raceId);
  return race?.information;
};

export const getRaceUnit = (submission, event) =>
  getRaceInfo(submission, event)?.raceUnit || 'km';

export const convertMetricLength = (metricLength, unit) => {
  return unit === 'km' ? metricLength : metricLength / KM_PER_MILE;
};

export const transformLengthForRace = (submission, event, metricLength) => {
  const unit = getRaceUnit(submission, event);

  return convertMetricLength(metricLength, unit);
};

const sumTotalMetricLength = (submissions) =>
  sum(map(submissions, 'metrics.distance')) / 1000.0;

export const getDistanceProgress = (
  submissions,
  { showUnit, showTargetLength, unit, targetLength },
) => {
  const totalSubmittedLengthInMetric = sumTotalMetricLength(submissions || []);
  const totalSubmittedLength = convertMetricLength(
    totalSubmittedLengthInMetric,
    unit,
  ).toFixed(2);
  const stat = targetLength
    ? Math.min(totalSubmittedLength, targetLength)
    : totalSubmittedLength;

  return `${stat}${showTargetLength ? `/${targetLength}` : ''}${
    showUnit ? unit : ''
  }`;
};

// @jrans This is wrong for multi-discipline. Each leg should cap the values and sum those parts.
// I could adapt the getLegProgress to handle this in the case but the results table doesn't
// have this functionality and aggregation on the backend and so I'd rather see consistency with a proper fix later.
// https://www.notion.so/letsdothis1/Putting-limit-on-the-max-distance-per-leg-554f8363e4554503ab9b4a3d3c03eb32
export const getParticipantProgress = (event, submissions, showUnit) => {
  if (!submissions?.length) {
    return null;
  }
  const submission = submissions[0];
  const targetLength = getRaceInfo(submission, event)?.raceLength;
  const unit = getRaceUnit(submission, event);

  return getDistanceProgress(submissions, {
    unit,
    targetLength,
    showTargetLength: true,
    showUnit,
  });
};

export const getLegProgress = (event, submissions, showUnit) => {
  if (!submissions?.length) {
    return null;
  }
  const submission = submissions[0];
  const race = event?.races?.races?.find((r) => r.id === submission.raceId);

  if (!race?.legDetails) {
    return null;
  }

  return race.legDetails.map(
    ({ id, distance, distanceUnit, discipline: disciplineId }) => {
      const total = getDistanceProgress(
        submissions.filter((s) => s?.legId === id),
        {
          unit: distanceUnit,
          targetLength: distance,
          showTargetLength: true,
          showUnit,
        },
      );
      const discipline = sport.find((s) => s.id === disciplineId)?.label;

      return { id, total, disciplineId, discipline };
    },
  );
};
